// =============================================================================
// Pure aggregation of POS sales rows into the series the company profile renders.
//
// Kept free of the Supabase client on purpose: these folds are where the real
// arithmetic lives (see the notes on foldMonthly), so they have to be testable
// without standing up a browser env or faking credentials. The I/O half is
// hooks/useCompanySalesMetrics.ts.
// =============================================================================

export interface MonthlyPoint {
  /** Month key, `YYYY-MM`. */
  month: string;
  /** Short Roman-numeral month label used on the chart axis. */
  label: string;
  netSales: number;
  receipts: number;
  /** Revenue divided by receipts — NOT an average of per-day averages. */
  avgReceipt: number;
}

export interface CategorySlice {
  name: string;
  netSales: number;
  /** Share of the period's total revenue, 0–100. */
  share: number;
}

export interface DailyRow {
  business_date: string;
  /**
   * Source channel, or `'all'` for the roll-up row.
   *
   * REQUIRED so the shape of a sales row says out loud that a total and a part
   * look identical without it: `lexshift_sales_daily` holds BOTH the per-source
   * rows and an `'all'` row that is their sum (`sales_agreguj`, migration 682).
   *
   * Note what this field is NOT: a compile-time gate. The Supabase client here
   * is created without a generated `Database` type, so `.select()` yields `any`
   * and the hook's `as DailyRow[]` assertion would swallow a missing column
   * either way. The thing that actually catches the mistake is the refusal in
   * `foldMonthly` below, at runtime, on real rows.
   */
  channel: string;
  net_sales: number | string | null;
  receipts: number | null;
}

/** The roll-up channel written by `sales_agreguj` — the sum of every source. */
export const KANAL_CALOSC = 'all';

export interface ProductRow {
  category_name: string | null;
  net_sales: number | string | null;
}

const MONTH_LABELS = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
] as const;

export const MAX_CATEGORY_SLICES = 5;

/** PostgREST returns `numeric` columns as strings; coerce defensively. */
function toNumber(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'number' ? v : Number.parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** First day of the window, `YYYY-MM-01`, `months` back from `from` inclusive. */
export function windowStart(from: Date, months: number): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() - (months - 1), 1));
  return d.toISOString().slice(0, 10);
}

/**
 * Fold daily rows into one point per month.
 *
 * THE ROLL-UP ROW IS NOT ANOTHER CHANNEL. `lexshift_sales_daily` carries, for
 * every day, one row per source AND an `'all'` row equal to their sum. Until
 * 2026-09-13 this function summed every row it was handed and the caller asked
 * for all of them, so the company profile showed the owner **exactly twice** the
 * revenue — measured on production, 2.000× in each of the last six months
 * (2026-07: 352 990,82 zł shown as 705 981,64 zł). The comment that used to
 * stand here called the summing deliberate; it was the justification of the bug,
 * not a description of the data.
 *
 * So this is the single point every sales row passes through, and it REFUSES an
 * ambiguous set rather than adding it up: a batch holding both the roll-up and
 * its own parts is the double count itself, and there is no total it could
 * honestly return. Callers decide which question they are asking — the whole
 * business (`channel = 'all'`) or a split by source (every other channel) — and
 * a caller that never decided now fails loudly instead of quietly doubling.
 *
 * The average basket stays total revenue over total receipts. Averaging the
 * per-day `avg_receipt` column instead weights a quiet day the same as a busy
 * one and produces a number that matches no receipt anybody issued.
 */
export function foldMonthly(rows: DailyRow[]): MonthlyPoint[] {
  const maCalosc = rows.some((r) => r.channel === KANAL_CALOSC);
  const maCzesci = rows.some((r) => r.channel !== KANAL_CALOSC);
  if (maCalosc && maCzesci) {
    throw new Error(
      'foldMonthly: zestaw zawiera i wiersz zbiorczy `all`, i wiersze źródeł — ' +
        'to jest podwójne liczenie, nie suma. Zapytaj o jedno albo o drugie.'
    );
  }

  const byMonth = new Map<string, { netSales: number; receipts: number }>();

  for (const row of rows) {
    if (!row.business_date) continue;
    const month = row.business_date.slice(0, 7);
    const acc = byMonth.get(month) || { netSales: 0, receipts: 0 };
    acc.netSales += toNumber(row.net_sales);
    acc.receipts += row.receipts ?? 0;
    byMonth.set(month, acc);
  }

  return [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, acc]) => ({
      month,
      label: MONTH_LABELS[Number.parseInt(month.slice(5, 7), 10) - 1] ?? month.slice(5, 7),
      netSales: acc.netSales,
      receipts: acc.receipts,
      avgReceipt: acc.receipts > 0 ? acc.netSales / acc.receipts : 0,
    }));
}

/**
 * Collapse product rows into the top categories plus a "Pozostałe" remainder.
 * Returns an empty list when the period has no revenue, so callers never render
 * a donut of NaN shares.
 */
export function foldCategories(rows: ProductRow[], limit = MAX_CATEGORY_SLICES): CategorySlice[] {
  const byCategory = new Map<string, number>();

  for (const row of rows) {
    const name = row.category_name?.trim() || 'Bez kategorii';
    byCategory.set(name, (byCategory.get(name) || 0) + toNumber(row.net_sales));
  }

  const sorted = [...byCategory.entries()]
    .map(([name, netSales]) => ({ name, netSales }))
    .filter((c) => c.netSales > 0)
    .sort((a, b) => b.netSales - a.netSales);

  const total = sorted.reduce((sum, c) => sum + c.netSales, 0);
  if (total <= 0) return [];

  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);
  const slices = [...head];

  if (tail.length > 0) {
    slices.push({
      name: 'Pozostałe',
      netSales: tail.reduce((sum, c) => sum + c.netSales, 0),
    });
  }

  return slices.map((c) => ({ ...c, share: (c.netSales / total) * 100 }));
}

/**
 * Month-over-month revenue change between the last two points, in percent.
 * Null when there is nothing to compare against, including a zero baseline.
 */
export function monthOverMonthChange(monthly: MonthlyPoint[]): number | null {
  if (monthly.length < 2) return null;
  const previous = monthly[monthly.length - 2];
  const latest = monthly[monthly.length - 1];
  if (previous.netSales <= 0) return null;
  return ((latest.netSales - previous.netSales) / previous.netSales) * 100;
}
